import React from 'react';

import { Seller, GenericFilterProps } from '../types';
import { useLocale } from 'locale';
import { PerformantCheckboxGroup } from '../common/PerformantCheckbox';
import FilterSection from '../common/DropdownSection';
import { isFilterActiveByType } from '../utils';
import styled from '@emotion/styled';
import { Section, SectionBody, SectionTitle, SecondaryTitle, PropertyTypeWrapper } from '../styled/common';
import { CheckboxChip, CheckboxChipGroup } from './amenities/checkbox-chip/CheckboxChip';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { without } from 'lodash';

import AgentIcon from 'assets/svg/combined-filters-search-view/Agent.svg';
import DeveloperIcon from 'assets/svg/combined-filters-search-view/Developer.svg';
import PrivateIcon from 'assets/svg/combined-filters-search-view/Private.svg';

const combinedFiltersSearchSellerToIcon: Partial<Record<Seller, React.ReactNode>> = {
  [Seller.Agent]: <AgentIcon />,
  [Seller.Developer]: <DeveloperIcon />,
  [Seller.Private]: <PrivateIcon />,
};

const Description = styled.span`
  color: ${({ theme }) => theme.colors.neutrals.grey4};
`;

const Sellers: React.FC<GenericFilterProps <'seller'>> = ({
   intermediateValue,
   setIntermediateValue,
   isMobile,
   isInline,
   commitValues,
   commitedValue,
   resetValue,
   type,
   config,
   alignment,
   initialValue,
 }) => {
  const { t } = useLocale();
  const { seller, filtersIcons } = config;

  const isCombinedFiltersSearchView = useScreenBreakpoint() < 3;

  const checkboxLabelGetter = (value: Seller) => {
    if (isCombinedFiltersSearchView) {
      const description = t('filters.sellerOptionsDescription', { value });
      if (value === Seller.Developer) return <span>{description.slice(1)}</span>;
      return <span>{t('filters.sellerOptions', { value })}</span>;
    }

    return (
      <span>
        {t('filters.sellerOptions', { value })}
        {<Description>{t('filters.sellerOptionsDescription', { value })}</Description>}
      </span>
    );
  };

  const checkboxGroup = (
    <PerformantCheckboxGroup
      values={intermediateValue}
      options={seller}
      labelGetter={checkboxLabelGetter}
      valueSetter={setIntermediateValue}
      mode={isCombinedFiltersSearchView ? 'black' : 'default'}
    />
  );

  if (isInline) {
    return (
      <Section combinedFiltersSearchMode={isCombinedFiltersSearchView} data-section-filter-name={type} data-auto={`filter-${type}`}>
        <SectionTitle isBold combinedFiltersSearchMode={isCombinedFiltersSearchView}>
          <div>
            {t('filters.seller', { count: null })}
            {!isCombinedFiltersSearchView && intermediateValue.length ? (
              <SecondaryTitle>
                {intermediateValue.length}
              </SecondaryTitle>
            ) : null}
          </div>
        </SectionTitle>
        <SectionBody combinedFiltersSearchMode={isCombinedFiltersSearchView} data-auto="filterCheckboxes">
          {isCombinedFiltersSearchView ? (
            <CheckboxChipGroup>
              {seller.map(item => (
                <CheckboxChip
                  key={item}
                  label={checkboxLabelGetter(item)}
                  icon={combinedFiltersSearchSellerToIcon[item]}
                  checked={intermediateValue.includes(item)}
                  onClick={() => setIntermediateValue(intermediateValue.includes(item)
                    ? without(intermediateValue, item)
                    : intermediateValue.concat(item))}
                />
              ))}
            </CheckboxChipGroup>
          ) : (
            <PropertyTypeWrapper isInline={!isMobile}>
              {checkboxGroup}
            </PropertyTypeWrapper>
          )}
        </SectionBody>
      </Section>
    );
  }

  return (
    <FilterSection
      isClearButtonDisabled={!isFilterActiveByType.seller(intermediateValue, initialValue)}
      type={type}
      alignment={alignment}
      icon={filtersIcons[type]}
      onClear={resetValue}
      label={t('filters.seller', { count: commitedValue.length })}
      onSubmit={commitValues}
      isActive={isFilterActiveByType.seller(commitedValue, initialValue)}
    >
      {checkboxGroup}
    </FilterSection>
  );
};

export default React.memo(Sellers);
